import { useState } from "react";
import { useLocalization } from "@fluent/react";
import { modules } from "byzantine";
import { useMappedEntryDescription } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { Button, ResponsiveFlex, Row, Select } from "@narmi/design_system";
import styles from "./InvoiceContainer.module.scss";
import * as formAdapters from "../../../src/adapters";
import SECDialog from "../ach/SECDialog";

/**
 * Invoice pay has no dedicated additional details step, so the SEC code and
 * entry description are collected here. Both bind to the shared ACH payment
 * form, so `scheduledPaymentBaseSchema` applies exactly as it does in the
 * standard ACH payment flow.
 */
const InvoiceAdditionalDetailsSection = () => {
  const { l10n } = useLocalization();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const codes = modules.achPayments.useSECCodes();
  const { mappedOptions } = useMappedEntryDescription();
  const { hasAdvancedDetailDefaults } =
    modules.achPayments.useAchPaymentSettingsDefaults();
  const [userToggledOpen, setUserToggledOpen] = useState<boolean | null>(null);
  const [showSECDialog, setShowSECDialog] = useState(false);

  const { errors, touched } = form;
  const hasError = Boolean(
    (touched.secCode && errors.secCode) ||
      (touched.entryDesc && errors.entryDesc)
  );

  const isOpen = hasError || (userToggledOpen ?? !hasAdvancedDetailDefaults);

  const toggleSECDialog = () => setShowSECDialog((wasOpen) => !wasOpen);
  const toggleSection = () => setUserToggledOpen(!isOpen);

  return (
    <>
      <ResponsiveFlex gapSize="s">
        <div className={styles.advancedDetailsToggle}>
          <Button
            kind="plain"
            size="s"
            label={l10n.getString("invoice-details-advanced-details")}
            endIcon={isOpen ? "chevron-up" : "chevron-down"}
            onClick={toggleSection}
          />
        </div>
        {isOpen ? (
          <ResponsiveFlex gapSize="s">
            <div>
              <Row alignItems="center" justifyContent="end">
                <Row.Item shrink>
                  <Button
                    label={l10n.getString(
                      "ach-payment-learn-more-transaction-types-cta"
                    )}
                    kind="plain"
                    size="xs"
                    onClick={toggleSECDialog}
                  />
                </Row.Item>
              </Row>
              <Select
                label={l10n.getString("label-payment-type")}
                disabled={codes.length === 0}
                {...formAdapters.select(form, "secCode")}
              >
                {codes.map((code) => (
                  <Select.Item key={`sec_code_${code}`} value={code}>
                    {code}
                  </Select.Item>
                ))}
              </Select>
            </div>
            <Select
              label={l10n.getString("label-entry-description")}
              disabled={mappedOptions.length === 0}
              {...formAdapters.select(form, "entryDesc")}
            >
              {mappedOptions.map(({ display, value }) => (
                <Select.Item key={value} value={value}>
                  {display}
                </Select.Item>
              ))}
            </Select>
          </ResponsiveFlex>
        ) : null}
      </ResponsiveFlex>
      <SECDialog
        isOpen={showSECDialog}
        handleClose={toggleSECDialog}
        codes={codes}
      />
    </>
  );
};

export default InvoiceAdditionalDetailsSection;
